








































import {
    defineComponent,
    ref,
    watch,
    computed,
    nextTick,
    onBeforeUnmount,
} from "@vue/composition-api";
import useMap from "@/utilities/useGoogleMaps";

export default defineComponent({
    props: {
        value: { type: Boolean, required: true },
        initialLat: { type: Number, required: true },
        initialLng: { type: Number, required: true },
        ageSeconds: { type: Number, required: false },
        hasInvalidAfterValid: { type: Boolean, default: false },
        geofenceRadiusMeters: { type: Number, required: false, default: 0 },
    },
    setup(props, { emit }) {
        const { googleMapApi } = useMap();
        const mapRef = ref<HTMLDivElement | null>(null);
        const map = ref<google.maps.Map | null>(null);
        const marker = ref<google.maps.Marker | null>(null);
        const circle = ref<google.maps.Circle | null>(null);
        const selectedLat = ref<number>(props.initialLat);
        const selectedLng = ref<number>(props.initialLng);
        const accuracyConfirmed = ref<boolean>(false);

        const ageText = computed(() => {
            if (props.ageSeconds == null) return "";
            const totalSeconds = Math.max(0, Math.floor(props.ageSeconds));
            const minutes = Math.floor(totalSeconds / 60);
            const seconds = totalSeconds % 60;
            return minutes > 0
                ? `${minutes}m ${seconds}s ago`
                : `${seconds}s ago`;
        });

        const initMap = async () => {
            if (!mapRef.value) return;

            if (map.value) {
                google.maps.event.trigger(map.value, "resize");
                map.value.setCenter({
                    lat: selectedLat.value,
                    lng: selectedLng.value,
                });
                return;
            }

            await googleMapApi.load();

            const center = {
                lat: selectedLat.value,
                lng: selectedLng.value,
            };

            map.value = new google.maps.Map(mapRef.value, {
                center,
                zoom: 16,
                mapTypeId: "roadmap",
                streetViewControl: false,
                mapTypeControl: false,
                fullscreenControl: false,
            });

            marker.value = new google.maps.Marker({
                position: center,
                map: map.value,
                draggable: true,
            });

            circle.value = new google.maps.Circle({
                map: map.value,
                center,
                radius: props.geofenceRadiusMeters || 0,
                strokeColor: "#ff0000",
                strokeOpacity: 0.8,
                strokeWeight: 2,
                fillColor: "#ff0000",
                fillOpacity: 0.2,
            });

            marker.value.addListener("dragend", () => {
                const pos = marker.value?.getPosition();
                if (pos) {
                    selectedLat.value = pos.lat();
                    selectedLng.value = pos.lng();
                    circle.value?.setCenter(pos);
                }
            });

            map.value.addListener("click", (e: google.maps.MapMouseEvent) => {
                if (!e.latLng) return;
                selectedLat.value = e.latLng.lat();
                selectedLng.value = e.latLng.lng();
                marker.value?.setPosition(e.latLng);
                circle.value?.setCenter(e.latLng);
            });
        };

        const resetSelection = () => {
            selectedLat.value = props.initialLat;
            selectedLng.value = props.initialLng;
            accuracyConfirmed.value = false;
        };

        const onDialogInput = (val: boolean) => {
            emit("input", val);
        };

        const confirm = () => {
            emit("confirm", {
                latitude: selectedLat.value,
                longitude: selectedLng.value,
                accuracyConfirmed: accuracyConfirmed.value,
            });
            emit("input", false);
        };

        const cancel = () => {
            emit("cancel");
            emit("input", false);
        };

        watch(
            () => props.value,
            (val) => {
                if (val) {
                    resetSelection();
                    nextTick(() => initMap());
                }
            }
        );

        watch(
            () => [props.initialLat, props.initialLng],
            () => {
                if (marker.value) {
                    marker.value.setPosition({
                        lat: props.initialLat,
                        lng: props.initialLng,
                    });
                }
                if (circle.value) {
                    circle.value.setCenter({
                        lat: props.initialLat,
                        lng: props.initialLng,
                    });
                }
                if (map.value) {
                    map.value.setCenter({
                        lat: props.initialLat,
                        lng: props.initialLng,
                    });
                }
            }
        );

        watch(
            () => props.geofenceRadiusMeters,
            (val) => {
                if (circle.value) {
                    circle.value.setRadius(val || 0);
                }
            }
        );

        onBeforeUnmount(() => {
            if (map.value) {
                google.maps.event.clearInstanceListeners(map.value);
                map.value = null;
            }
        });

        return {
            mapRef,
            ageText,
            accuracyConfirmed,
            onDialogInput,
            confirm,
            cancel,
        };
    },
});
